import { useQuery, useQueryClient, useMutation, UseQueryResult, UseMutationResult } from 'react-query';
import request from '../request';
import ServerErrorHandler from '../../ErrorHandler';
import { dispatchMessage, EventType } from 'app/useToasts';
import { i18n } from 'app/utils/i18n';
import { PermissionsResponse, AddPermissionInput, RemovePermissionInput, SuccessResponse } from './types';

export const permissionQueryKeys = {
  permission: 'PERMISSION',
  documentPermissions: 'document-permissions',
} as const;

export function useGetPermissions(documentId: string | undefined): UseQueryResult<PermissionsResponse> {
  return useQuery(
    [permissionQueryKeys.permission, documentId],
    async () => {
      const res = await request.get(`/gaby/documents/${documentId}/permissions`);
      return res.data;
    },
    {
      enabled: !!documentId,
      onError: ServerErrorHandler,
    }
  );
}

export function useAddPermissionToUser(): UseMutationResult<SuccessResponse, unknown, AddPermissionInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ documentId, permission, userId }: AddPermissionInput) => {
      const res = await request.post(`/gaby/documents/${documentId}/permissions/${permission}/users`, { userId });
      return res?.data as SuccessResponse;
    },
    {
      onError: (error) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: i18n('document-permissions.add-user.error'),
          removeTime: 4000,
        });
        ServerErrorHandler(error);
      },
      onSuccess: (_data, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('document-permissions.add-user.success.title'),
          removeTime: 4000,
        });
        queryClient.invalidateQueries([permissionQueryKeys.permission, variables.documentId]);
        queryClient.invalidateQueries([permissionQueryKeys.documentPermissions, variables.documentId]);
      },
    }
  );
}

export function useRemovePermissionFromUser(): UseMutationResult<SuccessResponse, unknown, RemovePermissionInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ documentId, permission, userId }: RemovePermissionInput) => {
      const res = await request.del(`/gaby/documents/${documentId}/permissions/${permission}/users/${userId}`);
      return res?.data as SuccessResponse;
    },
    {
      onError: (error) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: i18n('document-permissions.remove-user.error'),
          removeTime: 4000,
        });
        ServerErrorHandler(error);
      },
      onSuccess: (_data, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('document-permissions.remove-user.success.title'),
          removeTime: 4000,
        });
        queryClient.invalidateQueries([permissionQueryKeys.permission, variables.documentId]);
        queryClient.invalidateQueries([permissionQueryKeys.documentPermissions, variables.documentId]);
      },
    }
  );
}
